import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { accessLevelsApi } from '../../requests/accessLevels';
import { getCurrentAccess } from '../../access';
import Modal from '../Modal';
import AccessLevelEditor from './AccessLevelEditor';

/**
 * Access levels and what each may see and do. Only members whose level has
 * "Manage access levels" reach this page (route-access.js); only the Founder
 * changes a level, so everyone else sees it read-only. The server holds the
 * same lines and refuses changes that would lock everyone out.
 */
export default function AccessLevels() {
    const isFounder = getCurrentAccess().isFounder;
    const [levels, setLevels] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [error, setError] = useState(null);
    const [newOpen, setNewOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newCopyFrom, setNewCopyFrom] = useState('');
    const [busy, setBusy] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [unassigned, setUnassigned] = useState([]);

    const load = useCallback((selectId) => {
        setError(null);
        return Promise.all([accessLevelsApi.list(), accessLevelsApi.catalog(), accessLevelsApi.unassigned().catch(() => [])])
            .then(([list, groups, withoutLevel]) => {
                setLevels(list);
                setCatalog(groups);
                setUnassigned(withoutLevel);
                setSelectedId((current) => selectId || current || (list[0] && list[0].id));
            })
            .catch((e) => setError(e.message));
    }, []);

    useEffect(() => { load(); }, [load]);

    const selected = levels && levels.find((l) => l.id === selectedId);

    const create = () => {
        setBusy(true);
        setCreateError(null);
        accessLevelsApi.create({ name: newName, copy_from: Number(newCopyFrom) })
            .then((level) => {
                setNewOpen(false);
                setNewName('');
                return load(level.id);
            })
            .catch((e) => setCreateError(e.message))
            .then(() => setBusy(false));
    };

    const remove = (level) => {
        if (!window.confirm('Delete the "' + level.name + '" access level?')) return;
        accessLevelsApi.remove(level.id)
            .then(() => { setSelectedId(null); return load(); })
            .catch((e) => setError(e.message));
    };

    const saved = (level) => setLevels((all) => all.map((l) => (l.id === level.id ? level : l)));

    if (!levels || !catalog) {
        return (
            <div className="AccessLevels">
                <div className="al-container">
                    {error ? <div className="al-notice al-notice--error">{error}</div> : <div className="al-empty">Loading…</div>}
                </div>
            </div>
        );
    }

    return (
        <div className="AccessLevels">
            <div className="al-container">
                <div className="al-header">
                    <div className="al-header__headings">
                        <h1 className="al-header__title">Access Levels</h1>
                        <p className="al-header__subtitle">Choose what each access level can see and do.</p>
                    </div>
                    {isFounder && (
                        <button type="button" className="al-btn al-btn--primary"
                                onClick={() => { setNewCopyFrom(String(selectedId || levels[0].id)); setCreateError(null); setNewOpen(true); }}>
                            + New level
                        </button>
                    )}
                </div>
                <p className="al-muted">Members see changes the next time they load iCLIMB.</p>
                {unassigned.length > 0 && (
                    <section className="al-card al-group al-unassigned">
                        <h2 className="al-group__title">Members without a level ({unassigned.length})</h2>
                        <p className="al-muted">They can open almost nothing until they have one. Give each a level on their Account Settings tab.</p>
                        <ul className="al-members">
                            {unassigned.map((m) => (
                                <li key={m.id}>
                                    <Link to={arc.path.base + 'teamMember/' + m.id}>{m.name}</Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
                {error && <div className="al-notice al-notice--error">{error}</div>}
                <div className="al-body">
                    <div className="al-levels">
                        {levels.map((level) => (
                            <button key={level.id} type="button"
                                    className={'al-level' + (level.id === selectedId ? ' al-level--active' : '')}
                                    aria-current={level.id === selectedId ? 'true' : undefined}
                                    onClick={() => setSelectedId(level.id)}>
                                <span className="al-level__name">{level.name}</span>
                                {level.is_protected && <span className="al-level__tag">Protected</span>}
                                <span className="al-level__count">{level.members.length}</span>
                            </button>
                        ))}
                    </div>
                    {selected && (
                        <AccessLevelEditor
                            key={selected.id}
                            level={selected}
                            catalog={catalog}
                            readOnly={!isFounder}
                            onSaved={saved}
                            onDelete={() => remove(selected)}
                        />
                    )}
                </div>
                <Modal show={newOpen} onClose={() => setNewOpen(false)} className="al-modal">
                    <h2 className="al-modal__title">New access level</h2>
                    {createError && <div className="al-notice al-notice--error">{createError}</div>}
                    <label className="al-field">Name
                        <input className="al-input" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus/>
                    </label>
                    <label className="al-field">Start as a copy of
                        <select className="al-input" value={newCopyFrom} onChange={(e) => setNewCopyFrom(e.target.value)}>
                            {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    </label>
                    <div className="al-modal__actions">
                        <button type="button" className="al-btn" onClick={() => setNewOpen(false)}>Cancel</button>
                        <button type="button" className="al-btn al-btn--primary" disabled={busy || !newName.trim()} onClick={create}>Create</button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}
